import { Context, PaginatedResult, Id, mapDtoToEntity, enumMapToSame, isAppError, AppErrorCode } from '../../../common'
import {
  AddCitizenOpts,
  Citizen,
  CitizenCategory,
  CitizenFindOptions,
  CitizenFullInfo,
  CitizenSex,
  CitizensService,
  UpdateCitizenOpts,
} from './citizens'
import { AddCitizenDocument, CitizenDocument, DocumentType } from '../../citizenDocuments'
import { Api, CitizensApi, HttpClient } from '../../../api'
import { CardStatus } from '../../identityCards'

function mapDtoToCitizen(dto: Api.Citizen): Citizen {
  return mapDtoToEntity({
    ...dto,
    sex: enumMapToSame<Api.Citizen.Sex, CitizenSex>(dto.sex),
    category: enumMapToSame<Api.Citizen.Category, CitizenCategory>(dto.category),
    birthdate: new Date(dto.birthdate),
  })
}

function mapDtoToCitizenDocument(dto: Api.CitizenDocument, citizenId: string): CitizenDocument {
  return mapDtoToEntity({
    ...dto,
    date: dto.date ? new Date(dto.date) : undefined,
    type: enumMapToSame<Api.CitizenDocument.Type, DocumentType>(dto.type),
    citizenId: citizenId,
  })
}

function mapAddCitizenDocumentToDto(dto: AddCitizenDocument): Api.AddCitizenDocument {
  return {
    ...dto,
    date: dto.date ? dto.date.toISOString() : undefined,
    type: enumMapToSame<Api.AddCitizenDocument.Type, DocumentType>(dto.type),
  }
}

export class RemoteCitizensService implements CitizensService {
  private api: CitizensApi

  constructor(httpClient: HttpClient) {
    this.api = new CitizensApi(httpClient, undefined, '')
  }

  async findAll(ctx: Context, opts?: CitizenFindOptions | undefined): Promise<PaginatedResult<Citizen>> {
    const result = await this.api.citizensGetList(ctx, {
      ids: opts?.filter?.ids,
      municipalEntityId: opts?.filter?.municipalEntityId,
      search: opts?.filter?.search,
      aFrom: opts?.paging?.from,
      count: opts?.paging?.count,
      orderBy: opts?.order?.field,
      direction: opts?.order?.direction,
    })
    return {
      ...result.body,
      items: result.body.items.map(mapDtoToCitizen),
    }
  }

  async findAllWithInfo(ctx: Context, opts?: CitizenFindOptions): Promise<PaginatedResult<CitizenFullInfo>> {
    const result = await this.api.citizensWithInfoGetList(ctx, {
      ids: opts?.filter?.ids,
      municipalEntityId: opts?.filter?.municipalEntityId,
      cardStatus: opts?.filter?.cardStatus,
      month: opts?.filter?.month,
      year: opts?.filter?.year,
      search: opts?.filter?.search,
      aFrom: opts?.paging?.from,
      count: opts?.paging?.count,
      orderBy: opts?.order?.field,
      direction: opts?.order?.direction,
    })
    return {
      ...result.body,
      items: result.body.items.map(item => {
        return {
          citizen: mapDtoToCitizen(item.citizen),
          document: item.document,
          municipalEntity: item.municipalEntity,
          cardStatus: item.cardStatus as CardStatus,
        }
      }),
    }
  }

  async findById(ctx: Context, id: Id): Promise<Citizen | undefined> {
    try {
      const result = await this.api.citizensGetById(ctx, id)
      return mapDtoToCitizen(result.body.object)
    } catch (e) {
      if (isAppError(e) && e.code === AppErrorCode.OBJECT_NOT_FOUND) return
      throw e
    }
  }

  async findCitizenDocuments(ctx: Context, citizenId: Id): Promise<CitizenDocument[]> {
    const result = await this.api.citizenDocumentsGetListById(ctx, citizenId)
    return result.body.items.map(item => mapDtoToCitizenDocument(item, citizenId))
  }

  async add(ctx: Context, opts: AddCitizenOpts): Promise<Id> {
    const result = await this.api.citizensAdd(ctx, {
      ...opts,
      birthdate: opts.birthdate.toISOString(),
      documents: opts.documents.map(mapAddCitizenDocumentToDto),
    })
    return result.body.id
  }

  async addDependent(ctx: Context, dependentId: Id, guardianId: Id): Promise<void> {
    await this.api.citizenDependentsAdd(ctx, { dependentId: dependentId, guardianId: guardianId })
  }

  async removeDependent(ctx: Context, dependentId: Id, guardianId: Id): Promise<void> {
    await this.api.citizenDependentsDelete(ctx, { dependentId: dependentId, guardianId: guardianId })
  }

  async update(ctx: Context, id: Id, opts: UpdateCitizenOpts): Promise<void> {
    await this.api.citizensUpdate(ctx, id, {
      ...opts,
      birthdate: opts.birthdate ? opts.birthdate.toISOString() : undefined,
      documents: opts.documents ? opts.documents.map(mapAddCitizenDocumentToDto) : [],
    })
  }

  async delete(ctx: Context, id: Id): Promise<void> {
    await this.api.citizensDelete(ctx, id)
  }
}
